// Daily Ritual Components - Today's Smoke, Ash Wisdom, Pairing of the Week
import React, { useState } from 'react';
import {
  Flame,
  Clock,
  Sparkles,
  Wine,
  Coffee,
  ChevronRight,
  BookOpen,
  Star,
  Bookmark,
  Share2,
  X
} from 'lucide-react';
import { CigarIcon } from './Icons';
import {
  Cigar,
  CIGARS,
  getTodaysSmoke,
  getTodaysWisdom,
  getPairingOfTheWeek,
  getSimilarCigars
} from '../data/cigars';

// ============================================
// TODAY'S SMOKE CARD
// ============================================

interface TodaysSmokeCardProps {
  onSave?: (cigar: Cigar) => void;
  onLogSmoke?: (cigar: Cigar) => void;
  isSaved?: boolean;
}

export const TodaysSmokeCard: React.FC<TodaysSmokeCardProps> = ({
  onSave,
  onLogSmoke,
  isSaved = false,
}) => {
  const cigar = getTodaysSmoke();
  const [showDetails, setShowDetails] = useState(false);

  const strengthColors = {
    'mild': 'bg-green-500/20 text-green-400',
    'mild-medium': 'bg-lime-500/20 text-lime-400',
    'medium': 'bg-amber-500/20 text-amber-400',
    'medium-full': 'bg-orange-500/20 text-orange-400',
    'full': 'bg-red-500/20 text-red-400',
  };

  const strengthLabels = {
    'mild': 'Mild',
    'mild-medium': 'Mild-Medium',
    'medium': 'Medium',
    'medium-full': 'Medium-Full',
    'full': 'Full',
  };

  return (
    <>
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 rounded-2xl border border-amber-500/20 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-amber-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
              <Flame className="w-4 h-4 text-stone-900" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Today's Smoke</h3>
              <p className="text-white/50 text-xs">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
            </div>
          </div>
          <button
            onClick={() => onSave?.(cigar)}
            className={`p-2 rounded-lg transition-colors ${
              isSaved ? 'bg-amber-500/20 text-amber-500' : 'bg-white/5 text-white/50 hover:text-white'
            }`}
          >
            <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start gap-4">
            {/* Cigar Icon */}
            <div className="w-16 h-16 rounded-xl bg-stone-800 flex items-center justify-center flex-shrink-0">
              <CigarIcon className="w-8 h-8 text-amber-500" />
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-bold text-lg leading-tight">{cigar.brand}</h4>
              <p className="text-amber-500 text-sm font-medium">{cigar.line}</p>

              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${strengthColors[cigar.strength]}`}>
                  {strengthLabels[cigar.strength]}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white/70 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {cigar.timeCommitment} min
                </span>
              </div>
            </div>
          </div>

          {/* Flavor Profile */}
          <div className="mt-4">
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Primary Flavors</p>
            <div className="flex flex-wrap gap-1.5">
              {cigar.primaryFlavors.map(flavor => (
                <span key={flavor} className="px-2.5 py-1 rounded-lg bg-white/5 text-white/80 text-xs">
                  {flavor}
                </span>
              ))}
            </div>
          </div>

          {/* Ideal Moment */}
          {cigar.idealMoment && (
            <p className="mt-3 text-white/60 text-sm italic">"{cigar.idealMoment}"</p>
          )}

          {/* Action Buttons */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setShowDetails(true)}
              className="flex-1 py-2.5 rounded-xl bg-amber-500/20 text-amber-500 font-medium text-sm hover:bg-amber-500/30 transition-colors flex items-center justify-center gap-2"
            >
              <span>View Full Details</span>
              <ChevronRight className="w-4 h-4" />
            </button>
            {onLogSmoke && (
              <button
                onClick={() => onLogSmoke(cigar)}
                className="px-4 py-2.5 rounded-xl bg-green-500/20 text-green-400 font-medium text-sm hover:bg-green-500/30 transition-colors flex items-center justify-center gap-2"
                title="Log this smoke"
              >
                <Flame className="w-4 h-4" />
                <span className="hidden sm:inline">Log</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && (
        <CigarDetailModal
          cigar={cigar}
          onClose={() => setShowDetails(false)}
          onSave={onSave}
          isSaved={isSaved}
          onLogSmoke={onLogSmoke ? () => onLogSmoke(cigar) : undefined}
        />
      )}
    </>
  );
};

// ============================================
// ASH WISDOM CARD
// ============================================

interface AshWisdomCardProps {
  compact?: boolean;
}

export const AshWisdomCard: React.FC<AshWisdomCardProps> = ({ compact = false }) => {
  const wisdom = getTodaysWisdom();

  if (compact) {
    return (
      <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white/80 text-sm italic">"{wisdom.text}"</p>
            {wisdom.author && (
              <p className="text-white/40 text-xs mt-1">— {wisdom.author}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-2xl border border-white/10 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
        <h3 className="text-white font-semibold text-sm">Ash Wisdom</h3>
      </div>

      <blockquote className="text-white/90 text-lg leading-relaxed font-light">
        "{wisdom.text}"
      </blockquote>

      {wisdom.author && (
        <p className="text-amber-500 text-sm mt-3">— {wisdom.author}</p>
      )}

      <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
        <span className="text-white/40 text-xs capitalize">{wisdom.category}</span>
        <button className="text-white/40 hover:text-white transition-colors">
          <Share2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// PAIRING OF THE WEEK CARD
// ============================================

interface PairingOfTheWeekCardProps {
  onViewCigar?: (cigar: Cigar) => void;
}

export const PairingOfTheWeekCard: React.FC<PairingOfTheWeekCardProps> = ({ onViewCigar }) => {
  const pairing = getPairingOfTheWeek();

  const drinkIcons: Record<string, React.ReactNode> = {
    'coffee': <Coffee className="w-5 h-5" />,
    'whiskey': <Wine className="w-5 h-5" />,
    'bourbon': <Wine className="w-5 h-5" />,
    'scotch': <Wine className="w-5 h-5" />,
    'rum': <Wine className="w-5 h-5" />,
    'cognac': <Wine className="w-5 h-5" />,
    'wine': <Wine className="w-5 h-5" />,
    'beer': <Wine className="w-5 h-5" />,
    'non-alcoholic': <Coffee className="w-5 h-5" />,
  };

  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-indigo-600/10 rounded-2xl border border-purple-500/20 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-purple-500/20 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center text-white">
          {drinkIcons[pairing.drinkType] || <Wine className="w-4 h-4" />}
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">Pairing of the Week</h3>
          <p className="text-white/50 text-xs">Curated combination</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Pairing Display */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 text-center p-3 rounded-xl bg-white/5">
            <CigarIcon className="w-6 h-6 text-amber-500 mx-auto mb-1" />
            <p className="text-white font-medium text-sm">{pairing.cigar.brand}</p>
            <p className="text-white/50 text-xs">{pairing.cigar.line}</p>
          </div>
          <div className="text-white/30 text-xl">+</div>
          <div className="flex-1 text-center p-3 rounded-xl bg-white/5">
            <div className="text-purple-400 mx-auto mb-1 flex justify-center">
              {drinkIcons[pairing.drinkType]}
            </div>
            <p className="text-white font-medium text-sm">{pairing.drink}</p>
            <p className="text-white/50 text-xs capitalize">{pairing.drinkType}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-white/70 text-sm">{pairing.description}</p>

        {/* Why It Works */}
        <div className="mt-3 p-3 rounded-xl bg-white/5">
          <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Why It Works</p>
          <p className="text-white/80 text-sm">{pairing.whyItWorks}</p>
        </div>

        {/* Action */}
        <button
          onClick={() => onViewCigar?.(pairing.cigar)}
          className="mt-4 w-full py-2.5 rounded-xl bg-purple-500/20 text-purple-400 font-medium text-sm hover:bg-purple-500/30 transition-colors flex items-center justify-center gap-2"
        >
          <span>View Cigar Details</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// CIGAR DETAIL MODAL
// ============================================

interface CigarDetailModalProps {
  cigar: Cigar;
  onClose: () => void;
  onSave?: (cigar: Cigar) => void;
  isSaved?: boolean;
  onLogSmoke?: () => void;
}

export const CigarDetailModal: React.FC<CigarDetailModalProps> = ({
  cigar,
  onClose,
  onSave,
  isSaved = false,
  onLogSmoke,
}) => {
  const similarCigars = getSimilarCigars(cigar, 3);

  const strengthColors = {
    'mild': 'bg-green-500',
    'mild-medium': 'bg-lime-500',
    'medium': 'bg-amber-500',
    'medium-full': 'bg-orange-500',
    'full': 'bg-red-500',
  };

  const strengthLabels = {
    'mild': 'Mild',
    'mild-medium': 'Mild-Medium',
    'medium': 'Medium',
    'medium-full': 'Medium-Full',
    'full': 'Full',
  };

  const strengths = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'];
  const strengthIndex = strengths.indexOf(cigar.strength);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />

      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-stone-900 rounded-t-3xl sm:rounded-3xl border border-white/10">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-stone-900/95 backdrop-blur px-4 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-xl">{cigar.brand}</h2>
            <p className="text-amber-500 font-medium">{cigar.line}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl bg-white/5">
              <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-white font-semibold">{cigar.timeCommitment}</p>
              <p className="text-white/50 text-xs">minutes</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-white/5">
              <Flame className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-white font-semibold capitalize">{cigar.body}</p>
              <p className="text-white/50 text-xs">body</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-white/5">
              <Star className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-white font-semibold">{cigar.rating || '—'}</p>
              <p className="text-white/50 text-xs">rating</p>
            </div>
          </div>

          {/* Strength Meter */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Strength</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden flex">
                {strengths.map((s, i) => (
                  <div
                    key={s}
                    className={`flex-1 ${i <= strengthIndex ? strengthColors[cigar.strength] : ''} ${
                      i < strengths.length - 1 ? 'border-r border-stone-900' : ''
                    }`}
                  />
                ))}
              </div>
              <span className="text-white font-medium text-sm">{strengthLabels[cigar.strength]}</span>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">About</p>
            <p className="text-white/80 text-sm leading-relaxed">{cigar.description}</p>
          </div>

          {/* Composition */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Composition</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Wrapper</span>
                <span className="text-white">{cigar.wrapper}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Binder</span>
                <span className="text-white">{cigar.binder}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Filler</span>
                <span className="text-white">{cigar.filler.join(', ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Origin</span>
                <span className="text-white">{cigar.origin}</span>
              </div>
            </div>
          </div>

          {/* Flavor Notes */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Flavor Notes</p>
            <div className="flex flex-wrap gap-2">
              {cigar.flavorNotes.map((flavor, i) => (
                <span
                  key={flavor}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    i < 3 ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-white/70'
                  }`}
                >
                  {flavor}
                </span>
              ))}
            </div>
          </div>

          {/* Pairings */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Pairs Well With</p>
            <div className="flex flex-wrap gap-2">
              {cigar.pairings.map(pairing => (
                <span key={pairing} className="px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 text-sm">
                  {pairing}
                </span>
              ))}
            </div>
          </div>

          {/* Similar Cigars */}
          {similarCigars.length > 0 && (
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-2">If You Like This, Try</p>
              <div className="space-y-2">
                {similarCigars.map(similar => (
                  <div
                    key={similar.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5"
                  >
                    <CigarIcon className="w-5 h-5 text-amber-500" />
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">{similar.brand}</p>
                      <p className="text-white/50 text-xs">{similar.line}</p>
                    </div>
                    <span className="text-white/40 text-xs capitalize">{similar.strength}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => onSave?.(cigar)}
              className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
                isSaved
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30'
              }`}
            >
              <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
              {isSaved ? 'Saved' : 'Save to Humidor'}
            </button>
            {onLogSmoke && (
              <button
                onClick={onLogSmoke}
                className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium text-sm hover:bg-white/20 flex items-center justify-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Log Smoke
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// QUICK SMOKE SUGGESTION
// ============================================

interface QuickSmokeSuggestionProps {
  onSelect?: (cigar: Cigar) => void;
}

export const QuickSmokeSuggestion: React.FC<QuickSmokeSuggestionProps> = ({ onSelect }) => {
  const [suggestion, setSuggestion] = useState<Cigar | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateSuggestion = () => {
    setIsGenerating(true);
    // Simulate thinking
    setTimeout(() => {
      const hour = new Date().getHours();
      let strength: Cigar['strength'];

      // Time-based suggestion logic
      if (hour < 12) {
        strength = 'mild';
      } else if (hour < 17) {
        strength = 'medium';
      } else {
        strength = Math.random() > 0.5 ? 'medium-full' : 'full';
      }

      const candidates = CIGARS.filter(
        (c: Cigar) => c.strength === strength
      );
      const randomCigar = candidates[Math.floor(Math.random() * candidates.length)];
      setSuggestion(randomCigar);
      setIsGenerating(false);
    }, 800);
  };

  if (!suggestion) {
    return (
      <button
        onClick={generateSuggestion}
        disabled={isGenerating}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-stone-900 font-bold text-lg hover:from-amber-400 hover:to-orange-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
      >
        {isGenerating ? (
          <>
            <div className="w-5 h-5 border-2 border-stone-900/30 border-t-stone-900 rounded-full animate-spin" />
            <span>Finding your smoke...</span>
          </>
        ) : (
          <>
            <Flame className="w-5 h-5" />
            <span>Tonight's Smoke</span>
          </>
        )}
      </button>
    );
  }

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
      <div className="flex items-center justify-between mb-3">
        <span className="text-amber-500 text-sm font-medium">Tonight's Suggestion</span>
        <button
          onClick={() => setSuggestion(null)}
          className="text-white/50 text-xs hover:text-white"
        >
          Try another
        </button>
      </div>
      <div className="flex items-center gap-3">
        <CigarIcon className="w-8 h-8 text-amber-500" />
        <div className="flex-1">
          <p className="text-white font-bold">{suggestion.brand}</p>
          <p className="text-amber-500 text-sm">{suggestion.line}</p>
        </div>
        <button
          onClick={() => onSelect?.(suggestion)}
          className="px-4 py-2 rounded-lg bg-amber-500 text-stone-900 font-medium text-sm"
        >
          View
        </button>
      </div>
    </div>
  );
};

export default {
  TodaysSmokeCard,
  AshWisdomCard,
  PairingOfTheWeekCard,
  CigarDetailModal,
  QuickSmokeSuggestion,
};
